import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EcnMeta, EcnLine, CreateEcnInput, PartTimelineEntry } from '@bom/shared';
import { api } from '../lib/apiClient';

export function useEcns(tid: string | undefined) {
  return useQuery({
    queryKey: ['ecns', tid],
    queryFn: () => api.get<{ ecns: EcnMeta[] }>(`/tenants/${tid}/ecns`).then((r) => r.ecns),
    enabled: !!tid,
  });
}

export function useEcn(tid: string | undefined, ecnId: string | undefined) {
  return useQuery({
    queryKey: ['ecn', tid, ecnId],
    queryFn: () => api.get<{ ecn: EcnMeta; lines: EcnLine[] }>(`/tenants/${tid}/ecns/${ecnId}`),
    enabled: !!tid && !!ecnId,
  });
}

/** 料号 → ECN 时间线（点物料时调用） */
export function usePartTimeline(tid: string | undefined, partNo: string | undefined) {
  return useQuery({
    queryKey: ['part-timeline', tid, partNo],
    queryFn: () =>
      api
        .get<{ part_no: string; timeline: PartTimelineEntry[] }>(`/tenants/${tid}/ecns/timeline`, {
          query: { part_no: partNo! },
        })
        .then((r) => r.timeline),
    enabled: !!tid && !!partNo,
  });
}

/** 料号 → 最近影响它的 ECN（供 BOM 明细「有 ECN 变更」标记） */
export interface AffectedPart {
  ecn_id: string;
  ecn_date: string;
  remark?: string;
}
export function useAffectedParts(tid: string | undefined) {
  return useQuery({
    queryKey: ['affected-parts', tid],
    queryFn: () =>
      api
        .get<{ parts: Record<string, AffectedPart> }>(`/tenants/${tid}/ecns/affected-parts`)
        .then((r) => r.parts),
    enabled: !!tid,
  });
}

export function useResendEcnNotify(tid: string) {
  return useMutation({
    mutationFn: (vars: { ecnId: string; subject?: string; body?: string }) =>
      api.post<{ notified: boolean; recipients: number; error?: string }>(
        `/tenants/${tid}/ecns/${vars.ecnId}/notify`,
        {
          ...(vars.subject ? { subject: vars.subject } : {}),
          ...(vars.body ? { body: vars.body } : {}),
        },
      ),
  });
}

export function useCreateEcn(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEcnInput) =>
      api.post<{ id: string; notified: boolean; notify_error?: string }>(
        `/tenants/${tid}/ecns`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ecns', tid] });
      qc.invalidateQueries({ queryKey: ['part-timeline', tid] });
      qc.invalidateQueries({ queryKey: ['affected-parts', tid] });
    },
  });
}

export function useDeleteEcn(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ecnId: string) => api.del<{ deleted: number }>(`/tenants/${tid}/ecns/${ecnId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ecns', tid] });
      qc.invalidateQueries({ queryKey: ['part-timeline', tid] });
      qc.invalidateQueries({ queryKey: ['affected-parts', tid] });
    },
  });
}

/** 下载 ECN 原始文件（新分页打开 presigned URL） */
export async function openEcnFile(tid: string, ecnId: string): Promise<void> {
  const r = await api.get<{ download_url: string }>(`/tenants/${tid}/ecns/${ecnId}/download`);
  window.open(r.download_url, '_blank', 'noopener');
}
